"use client";

import { TooltipPortal } from "@radix-ui/react-tooltip";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@workspace/ui/components/tooltip";
import { cn } from "@workspace/ui/lib/utils";
import { useCallback, useState, useSyncExternalStore } from "react";

type TextWithTooltipProps = {
  text: string | number;
  className?: string;
};

function subscribeTruncation(
  element: HTMLElement | null,
  callback: () => void
): () => void {
  if (!element) {
    return () => 0;
  }
  const observer = new ResizeObserver(callback);
  observer.observe(element);
  return () => observer.disconnect();
}

function getTruncationSnapshot(element: HTMLElement | null): boolean {
  if (!element) {
    return false;
  }
  return element.scrollWidth > element.clientWidth;
}

export function TextWithTooltip({ text, className }: TextWithTooltipProps) {
  const [element, setElement] = useState<HTMLDivElement | null>(null);

  const subscribe = useCallback(
    (callback: () => void) => subscribeTruncation(element, callback),
    [element]
  );
  const getSnapshot = useCallback(
    () => getTruncationSnapshot(element),
    [element]
  );

  const isTruncated = useSyncExternalStore(subscribe, getSnapshot, () => false);

  return (
    <TooltipProvider delayDuration={100} disableHoverableContent>
      <Tooltip>
        <TooltipTrigger asChild disabled={!isTruncated}>
          <div
            className={cn(
              "truncate",
              !isTruncated && "pointer-events-none",
              className
            )}
            ref={setElement}
          >
            {text}
          </div>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent>{text}</TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
}
